// FE-12 — modelo de dominio de gastos y recibos (ADR-015, backend BE-28).
//
// Cuatro ideas gobiernan todo lo de abajo, y ninguna se deduce de los campos:
//
//  1. **El importe es TEXTO, no un número.** El `Number` de JavaScript es un
//     float64 y no representa 0,1 con exactitud; esta cifra acaba en un reporte
//     contable. El contrato lo declara `Money` con patrón
//     `^[0-9]{1,12}(\.[0-9]{1,2})?$`. Sumarlos con `Number` es exactamente lo
//     que el backend evitó declarándolo texto — `utils/expenses.ts` los suma en
//     centavos enteros.
//
//  2. **`incurredAt` no es `createdAt`.** El primero es cuándo se INCURRIÓ el
//     gasto; el segundo, cuándo alguien lo digitó. Un peaje de septiembre
//     registrado en octubre pertenece a septiembre — y es `incurredAt` quien
//     decide a qué mes y a qué viaje pertenece.
//
//  3. **El viaje no se estampa, se deriva.** Un gasto aparece en el expediente
//     de un viaje porque su `incurredAt` cae dentro de la ventana (ADR-024), no
//     porque alguien lo haya asignado. `tripId` existe, es opcional, y significa
//     **intención**: cuando está, manda sobre la ventana.
//
//  4. **El recibo se VERSIONA, no se reemplaza.** Cada subida crea un objeto
//     nuevo y una fila nueva; la vigente es la última y las anteriores no se
//     pueden borrar. Una foto mal tomada sobrevive como auditoría (ADR-009
//     exige write-once, y corregir es operación de primera clase).

export interface ExpenseCategory {
  id: string;
  /** Identificador ESTABLE en mayúsculas (`COMBUSTIBLE`, `PEAJE`). Lo referencian los reportes y no se corrige. */
  code: string;
  label: string;
  /** Desactivada se retira del formulario, pero los gastos que ya la usan no cambian. */
  active: boolean;
}

/** Única divisa del MVP (ADR-015). Multi-divisa está fuera de alcance. */
export const CURRENCY = 'COP';

/**
 * Una versión del recibo. `integrityHash` es el SHA-256 que calculó el CORE
 * sobre los bytes que recibió — no sobre el archivo que salió de la cámara.
 * Esta app comprime la foto antes de subirla (ver `utils/receiptImage.ts`), así
 * que el hash corresponde al JPEG comprimido. Es la decisión del dueño del
 * 2026-09-11, no un descuido.
 */
export interface Receipt {
  id: string;
  contentType: string;
  sizeBytes: number;
  /** SHA-256 en hexadecimal del archivo tal como se guardó (ADR-009). */
  integrityHash: string;
  /** userId de quien la subió. */
  uploadedBy: string;
  uploadedAt: string;
}

export interface Expense {
  id: string;
  categoryId: string;
  categoryCode: string;
  categoryLabel: string;
  /** Importe como TEXTO canónico con dos decimales: `"50000.00"`. Ver la idea 1. */
  amount: string;
  currency: string;
  /** Cuándo se INCURRIÓ el gasto. El instante de escritura es `createdAt`. */
  incurredAt: string;
  vehicleId?: string;
  vehiclePlate?: string;
  subUnitId?: string;
  driverId?: string;
  driverName?: string;
  /** Presente solo si alguien DECLARÓ el viaje. Su ausencia no saca el gasto del expediente. */
  tripId?: string;
  notes?: string;
  /** userId de quien lo registró. */
  registeredBy: string;
  createdAt: string;
  updatedAt?: string;
  /** La versión VIGENTE del recibo. Ausente = el gasto no tiene soporte todavía. */
  receipt?: Receipt;
}

/** Un gasto tiene que colgar de al menos uno: uno suelto no se puede reportar ni auditar. */
export function tieneAsociacion(e: { vehicleId?: string; driverId?: string; tripId?: string }): boolean {
  return Boolean(e.vehicleId || e.driverId || e.tripId);
}

export type ExpenseAction = 'REGISTERED' | 'CORRECTED';

/** El "de qué a qué" de un campo corregido. Una asociación retirada deja `to` vacío. */
export interface ExpenseChange {
  field: string;
  from: string;
  to: string;
}

/**
 * Un paso de la novedad del gasto. Es append-only por privilegio en la base:
 * ni el core puede reescribirla.
 *
 * `occurredAt` aquí es el reloj de pared de la ANOTACIÓN, no el `incurredAt`
 * del gasto. El contrato reutiliza el nombre; este tipo lo llama `recordedAt`
 * para que nadie los mezcle, igual que hizo `LogbookTransition`.
 */
export interface ExpenseTransition {
  action: ExpenseAction;
  changes: ExpenseChange[];
  /** userId, o `system`. */
  actor: string;
  /** Obligatoria en una corrección; ausente en el registro inicial. */
  reason?: string;
  recordedAt: string;
}

/** Los campos que el PATCH puede dejar sin valor. Se quitan con `unset`, NUNCA con `null`. */
export type ExpenseUnsettable = 'vehicleId' | 'driverId' | 'tripId' | 'notes';
